/* eslint-disable max-lines-per-function */
import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, TextField, IconButton, Grid, Autocomplete, FormControl } from '@mui/material';
import { useLang } from '~/hooks/useLang';
import { authFetch } from '~/services/fetch';
import { useHookstate, State, StateMethods } from '@hookstate/core';
import notify from '~/utils/notify';
import Loading from '../../../../../components/Loading';
import { checkTrackingDevice, getDataByPlate } from './services/index'
import CloseIcon from '@mui/icons-material/Close';
import useVehicleState from '~/features/Vehicle/stores/VehicleState';
import { lengthLimit, removeMask } from '~/features/Dispatch/utils/stringsManipulations';
import { LoadingButton } from '@mui/lab';
import { CrewComposition, DeviceLog } from '../DisplayDispatchDevice';
import { useListAllVehicleState } from '../../../../../components/ListAllVehicle';
import useSystemState from '~/features/System/stores/SystemState';
import FilterAutocompleteCity from '../../../../../components/FilterSelectAutocompleteCity';
import FilterAutocompleteOperation from '../../../../../components/FilterAutocompleteOperation';
import type { checkTrackingResponse } from '~/components/ListVehiclesSocket/interfaces/IListVehiclesProps';
import UploadImageButton from './components/UploadImageButton';
import CrewFormConnectDevice from './components/CrewFormConnectDevice';
import {
  createDefaultCommander,
  createDefaultDriver,
  createDefaultErrorState,
  createDefaultVehicle,
  createDefaultDeviceSituationOptions,
} from './constants'
import type { IErrorConectDispatchVehicle, IUserCrew, IVehicleConnect, optionSituationDeviceProps } from '../../interfaces/IListDevicesprops';

export interface IConnectDispatchVehicle {
  readonly openState: StateMethods<boolean>;
  readonly identification: string;
  readonly deviceId: StateMethods<number | null>;
  readonly plate?: string | null;
  readonly lockTemporaryTracking?: boolean;
  readonly crew?: State<readonly CrewComposition[] | null>
  readonly deviceLog?: State<DeviceLog | null>;
  readonly handleLoadDevices?: () => void;
  readonly labelCommander?: string | null;
  readonly labelDriver?: string | null;
  readonly labelPatrolmans?: string | null;
  readonly labelKmInitial?: string | null;
  readonly trackingId?: string | null;
  readonly editable?: boolean;
}


export default function ConectDispatchVehicle({ lockTemporaryTracking = false, openState, identification, deviceId, plate, crew, deviceLog, labelCommander, labelDriver, labelPatrolmans, labelKmInitial, trackingId, editable = false }: IConnectDispatchVehicle) {
  const { translate } = useLang();
  const { updateCrewComposition } = useVehicleState();
  const { toggleRefresh } = useListAllVehicleState();
  const { systemConfig } = useSystemState();
  const checkTrackingResponseState = useHookstate<checkTrackingResponse | null>(null)
  const isOpenDialogAlert = useHookstate(false)
  const textWarning = useHookstate<string | null>(null)
  const optionSituationDevice = useHookstate(createDefaultDeviceSituationOptions());
  const deviceCondition = useHookstate<number | null>(null);
  const fileMediaDevice = useHookstate<string | null>(null);
  const [value, setValue] = useState<optionSituationDeviceProps | null>(null)
  const commander = useHookstate<IUserCrew>(createDefaultCommander());
  const driver = useHookstate<IUserCrew>(createDefaultDriver());
  const patrolmans = useHookstate<ReadonlyArray<IUserCrew>>([]);
  const deviceLogId = useHookstate<number | null>(null);
  const error = useHookstate<IErrorConectDispatchVehicle>(createDefaultErrorState());
  const isLoadingContent = useHookstate(false);
  const loading = useHookstate(false);

  const requiredFields = useHookstate({
    km: systemConfig().requiredKm.get() === '1',
    phone: systemConfig().requiredPhone.get() === '1',
    enableIntegrationSystemRh: systemConfig().enableIntegrationSystemRh.get() === '1',
    upLoadImage: systemConfig().featureToPocRs.get() === '1',
    deviceSituation: systemConfig().featureToPocRs.get() === '1'
  });

  const vehicle = useHookstate<IVehicleConnect>({
    km: deviceLog?.get()?.km_initial ?? '',
    task: deviceLog?.get()?.task ?? '',
    trackingId: deviceLog?.get()?.tracking_id ?? '',
    notes: deviceLog?.get()?.notes ?? '',
    cityId: deviceLog?.get()?.city?.id ?? null,
    operationId: deviceLog?.get()?.operation?.id ?? null,
  });

  const resetValues = () => {
    vehicle.set(createDefaultVehicle());
    commander.set(createDefaultCommander());
    driver.set(createDefaultDriver());
    error.set(createDefaultErrorState());
    deviceLogId.set(null);
    patrolmans.set([]);
  }

  const handleCancel = () => {
    resetValues();
    openState.set(false);
  };

  const verifyRequiredFields = () => {
    error.returnError.set('');
    if (!vehicle.km.get() && requiredFields.km.get()) {
      error.km.set(translate('Inform a Km'));
    } else if (commander?.cpf?.get()?.replace(/\D/gi, '').length !== 11) {
      error.commander.cpf.set(translate('Inform a commander'));
    } else if (requiredFields.phone.get() && commander?.phone?.get()?.replace(/\D/gi, '').length !== 11) {
      error.commander.phone.set(translate('Inform a phone'));
    } else if (error.returnError.get()) {
      return
    } else if (crew) {
      update()
    } else {
      send()
    }
  }

  const update = () => {
    loading.set(true)
    updateCrewComposition({
      deviceLogId: deviceLogId.get(),
      crewComposition: {
        commanderCPF: commander.get().cpf,
        driverCPF: driver.get().cpf,
        patrolmansCPF: patrolmans.get().map(patrolman => patrolman.cpf).filter(patrolmanCPF => patrolmanCPF)
      },
      deviceLog: {
        deviceId: deviceId.get() ?? null,
        km: vehicle.get()?.km,
        phone: removeMask(commander.get().phone ?? null),
        task: vehicle.get().task,
        notes: vehicle.get().notes,
        trackingId: vehicle.get().trackingId,
        cityId: vehicle.cityId.get(),
        operationId: vehicle.operationId.get(),
      }
    }).then((response) => {
      loading.set(false)
      notify({ message: translate('Saved successfully'), type: 'success' });
      deviceLog?.set(response.deviceLog)
      crew?.set(response.crewComposition)
      toggleRefresh()
      return handleCancel();
    })
      .catch((err) => {
        console.error(err);
        loading.set(false)
        return;
      })
  }

  const send = () => {
    loading.set(true)
    authFetch({
      url: '/auth/operator/login/device',
      data: {
        commander: commander?.cpf?.get()?.replace(/\D/gi, ''),
        device_id: deviceId.get(),
        km_initial: vehicle.km.get(),
        task: vehicle.task.get(),
        notes: vehicle.notes.get(),
        tracking_id: vehicle.trackingId.get(),
        phone: commander?.phone?.get()?.replace(/\D/gi, ''),
        driver: driver?.cpf?.get()?.replace(/\D/gi, ''),
        patrolmans: patrolmans?.get().map(({ cpf }) => {
          return cpf?.replace(/\D/gi, '')
        }, []),
        city_id: vehicle.cityId.get(),
        operation_id: vehicle.operationId.get(),
        device_condition: deviceCondition.get(),
        file: fileMediaDevice.get()
      }
    }).then(() => {
      loading.set(false)
      notify({ message: translate('Device conected Sucessfully!'), type: 'success' });
      return handleCancel();
    }).catch((err) => {
      loading.set(false)
      error.returnError.set(translate(`${err}`))
    });
  }

  const findRHdataByPlate = (plate) => {
    isLoadingContent.set(true);
    getDataByPlate(plate).then((response) => {


      if (response.driver != null) {
        driver?.cpf.set(response.driver.cpf);
        driver?.name.set(response.driver.name);
        driver?.representation_name.set(response.driver.representation_name)
        driver?.phone.set(response.driver.phone);
        driver?.registration.set(response.driver.registration);

        const position = driver?.position.get() ?? '';

        if (response.driver.alreadyConnected) {
          error[position].cpf.set(`${response.driver.representation_name} ${translate('already logged in at')} ${response.driver.device ?? translate('a station')}`)
          driver?.set({ position, cpf: '', representation_name: '', name: '', phone: '', registration: '' })
        }

      } else {
        notify({ message: translate('Empty Driver Return'), type: 'error' })
      }

      if (response.commander != null) {
        commander?.cpf.set(response.commander.cpf);
        commander?.name.set(response.commander.name);
        commander?.representation_name.set(response.commander.representation_name);
        commander?.phone.set(response.commander.phone);
        commander?.registration.set(response.commander.registration);

        const position = commander?.position.get() ?? '';

        if (response.commander.alreadyConnected) {
          error[position].cpf.set(`${response.commander.representation_name} ${translate('already logged in at')} ${response.commander.device ?? translate('a station')}`)
          commander?.set({ position, cpf: '', name: '', representation_name: '', phone: '', registration: '' })
        }

      } else {
        notify({ message: translate('Empty Commander Return'), type: 'error' })
      }

      if (response.patrolmans.length > 0) {

        const patrolmanParsed = response.patrolmans.map((patrolman) => {
          const position = 'patrolmans';

          if (response.commander.alreadyConnected) {
            // eslint-disable-next-line functional/immutable-data
            error[position].cpf.set(`${response.commander.representation_name} ${translate('already logged in at')} ${response.commander.device ?? translate('a station')} \n` + error[position].cpf.get())
            commander?.set({ position, cpf: '', name: '', representation_name: '', phone: '', registration: '' })

            return { position: position, cpf: '', name: '', phone: '', registration: '' };
          }

          return ({ cpf: patrolman.cpf, name: patrolman.name, representation_name: patrolman.representation_name, registration: patrolman.registration, position: position })
        }).filter((item) => item.cpf)

        patrolmans?.set(patrolmanParsed);

      } else {
        notify({ message: translate('Empty Patrolmans Return'), type: 'error' })
      }

      isLoadingContent.set(false);
    }).catch((err) => {
      isLoadingContent.set(false);
    })
  }

  useEffect(() => {
    if (crew) {
      const comandaderCrew = crew?.get()?.find(crewFind => crewFind.position.id === 1);
      const driverCrew = crew?.get()?.find(crewFind => crewFind.position.id === 2);
      const patrolmansCrew = crew?.get()?.filter(component => component.position.id === 3).map(crew => {
        return {
          cpf: crew.user?.cpf ?? '',
          name: crew.user?.name ?? '',
          representation_name: crew.user?.representation_name ?? '',
          registration: crew.user?.registration ?? '',
          position: 'patrolmans',
        }
      })

      commander.set({
        ...commander.get(),
        cpf: comandaderCrew?.user?.cpf ?? '',
        name: comandaderCrew?.user?.name ?? '',
        representation_name: comandaderCrew?.user?.representation_name ?? '',
        phone: deviceLog?.get()?.phone ?? '',
        registration: comandaderCrew?.user?.registration ?? '',
        error: '',
      })

      deviceLogId.set(comandaderCrew?.device_log_id ?? null);

      driver.set({
        ...driver.get(),
        cpf: driverCrew?.user?.cpf ?? '',
        name: driverCrew?.user?.name ?? '',
        representation_name: driverCrew?.user?.representation_name ?? '',
        registration: driverCrew?.user?.registration ?? '',
        error: ''
      })
      patrolmans.set(patrolmansCrew ?? [])
    }
  }, [])

  useEffect(() => {
    if (openState.get() && deviceId.get()) {
      checkTrackingResponseState.set(null)

      checkTrackingDevice({ deviceId: deviceId.get() }).then((response) => {
        if (response) {
          checkTrackingResponseState.set(response.data)
          checkOpenDialogAlert()
        }
      })
    }

  }, [openState.get()])

  const checkOpenDialogAlert = () => {
    textWarning.set(null)
    if (checkTrackingResponseState.get()?.need_update_tracking && !checkTrackingResponseState.get()?.other_device_is_connected) {
      isOpenDialogAlert.set(true)
      textWarning.set(`Com base em integração externa, ${checkTrackingResponseState.get()?.tracking_id ? `este dispositivo terá id de rastreamento atualizado automaticamente para ${checkTrackingResponseState.get()?.tracking_id}.` : `este dispositivo não possui id de rastreamento associado, portanto o id de rastreamento ${trackingId} cadastrado será removido.`}`)
    }

    if (checkTrackingResponseState.get()?.need_update_tracking && checkTrackingResponseState.get()?.other_device_is_connected) {
      isOpenDialogAlert.set(true)
      textWarning.set(`Com base em integração externa, o id de rastreamento deste dispositivo é ${checkTrackingResponseState.get()?.tracking_id}, porém não poderá ser atualizado automaticamente pois está vinculado ao dispositivo ${(checkTrackingResponseState.get()?.other_device_name)} que encontra-se conectado. Para que a atualização seja feita, é necessária a desconexão do dispositivo ${(checkTrackingResponseState.get()?.other_device_name)}, caso contrário a conexão deste dispositivo será feita sem rastreamento associado.`)
    }
  }

  return (
    <>
      <Dialog
        open={openState.get()}
        maxWidth='sm'
      >
        <IconButton
          title={translate('Close')}
          size="small"
          onClick={() => handleCancel()}
          sx={{
            position: 'absolute',
            right: '10px',
            top: '15px',
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle>
          <Typography variant='h5' sx={{ textAlign: 'center' }}>
            {`${translate('Device:name')} - ${identification}`}

            <Typography variant='subtitle2'>
              {`${translate('Tracking registered')}: ${trackingId ? trackingId : translate('Not informed')}`}
            </Typography>
          </Typography>
          {<Grid container direction='row'
            sx={{ textAlign: 'center', pt: 2 }}
          >
            {requiredFields.enableIntegrationSystemRh.get() && (
              <Grid item xs={12}>
                <Button variant="contained"
                  disabled={!plate}
                  onClick={() => { findRHdataByPlate(plate); }}>
                  {translate('Automatic Insertion')}
                </Button>
              </Grid>
            )}
            <Grid item xs={12}>
              {!!error.returnError.get() && <span style={{
                fontSize: 14,
                color: 'red',
              }}>
                {`${error.returnError.get()}`}
              </span>}
            </Grid>
          </Grid>}
        </DialogTitle>
        <DialogContent sx={{ minHeight: '300px', placeItems: 'center' }} dividers>
          <Grid container
            direction='row'
            sx={{ justifyContent: 'center', alignItems: 'flex-start', alignContent: 'center' }}
          >
            <Loading visible={isLoadingContent.get()} />
            <Grid item xs={12} sx={{ pb: 2 }}>
              <Typography>
                {translate('Device:name')}
                {requiredFields.km.get() ? !!error.km.get() && <span style={{ color: 'red' }}>
                  {` (${error.km.get()})`}
                </span> : ''}
              </Typography>
            </Grid>
            <Grid container direction='column' xs={6}>
              <Grid item>
                <TextField fullWidth
                  id="initial-km"
                  type='number'
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                  label={labelKmInitial ? labelKmInitial : ('Km')}
                  name="km"
                  size='small'
                  value={vehicle.km.get()}
                  onBlur={() => {
                    if (requiredFields.km.get()) {
                      if (!vehicle.km.get()) {
                        error.km.set(translate('Inform a Km'));
                      }
                    }
                  }}
                  onChange={(event) => {
                    error.km.set('');
                    vehicle.km.set(event.target.value);
                    if (requiredFields.km.get()) {
                      if (!vehicle.km.get().length) {
                        error.km.set(translate('Inform a Km'));
                      }
                    }
                  }}
                  required={requiredFields.km.get()}
                  error={requiredFields.km.get() ? !!error.km.get() : false} />
              </Grid>
              {!lockTemporaryTracking &&
                <Grid item sx={{ pt: 0.75 }}>
                  <TextField fullWidth
                    id="tracking-id"
                    label={translate('Temporary tracking')}
                    name="tracking-id"
                    size='small'
                    value={vehicle.trackingId.get()}
                    onChange={(event) => {
                      vehicle.trackingId.set(lengthLimit(event.target.value, 10));
                    }} />
                </Grid>}
              <Grid item sx={{ pt: 0.75 }}>
                <TextField fullWidth
                  id='task'
                  size='small'
                  label={translate('Task')}
                  name="task"
                  value={vehicle.task.get()}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => vehicle.task.set(event.target.value)} />
              </Grid>
              <Grid item sx={{ pt: 0.75 }}>
                <FilterAutocompleteCity
                  onCityChange={(city) => {
                    vehicle.cityId.set(city);
                  }}
                  cityId={vehicle.cityId?.get()} />
              </Grid>
              <Grid item sx={{ pt: 0.75 }}>
                <FilterAutocompleteOperation
                  operationActive={true}
                  onOperationChange={(operation) => { vehicle.operationId.set(operation); }}
                  operationId={vehicle.operationId?.get()} />
              </Grid>
              {requiredFields.deviceSituation.get() && !editable &&
                <Grid item sx={{ pt: 0.75 }}>
                  <FormControl fullWidth size='small' >
                    <Autocomplete
                      id='filter-situation_device'
                      options={optionSituationDevice.get()}
                      disabled={false}
                      value={value}
                      noOptionsText={translate('No options')}
                      size='small'
                      loading={loading.get()}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      getOptionLabel={(option) => option.name ?? ''}
                      onChange={(_, operation) => {
                        setValue(operation?.name ? operation : null);
                        deviceCondition.set(operation?.id ?? null);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={translate('Situação da viatura')}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
              }
            </Grid>

            <Grid item xs={6} sx={{ pl: 1 }}>
              <TextField fullWidth
                id='notes'
                size='small'
                label={translate('Notes')}
                name="notes"
                rows='9'
                value={vehicle.notes.get()}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => vehicle.notes.set(event.target.value)}
                multiline />
            </Grid>
            {requiredFields.upLoadImage.get() && !editable &&
              <Grid item xs={12} sx={{ pt: 0.75 }}>
                <UploadImageButton fileMediaDevice={fileMediaDevice} />
              </Grid>
            }
            <CrewFormConnectDevice
              labelCommander={labelCommander}
              deviceId={deviceId}
              labelDriver={labelDriver}
              labelPatrolmans={labelPatrolmans}
              commander={commander}
              driver={driver}
              patrolmans={patrolmans}
              error={error}
              direction={'column'}
              requiredFields={requiredFields}
            />
          </Grid>
        </DialogContent>
        <DialogActions sx={{ padding: "16px" }}>
          <LoadingButton
            disabled={loading.get()}
            size="small"
            variant='contained'
            loading={loading.get()}
            title={translate('Save')}
            loadingPosition="center"
            onClick={verifyRequiredFields}>
            {translate("Save")}
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <Dialog open={isOpenDialogAlert.get()}>
        <IconButton
          title={translate('Close')}
          size="small"
          onClick={() => isOpenDialogAlert.set(false)}
          sx={{
            position: 'absolute',
            right: '10px',
            top: '15px',
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle padding={2} color={'red'}>
          {translate('Warning!')}
        </DialogTitle>
        <DialogContent dividers>
          <Typography>
            {textWarning.get()}
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  )
}
